import type {SoftCatalogRegion, SoftCatalogType} from 'types/soft_provision';

/** Stamped by UX (loxilb-ui#35): "<id> — <name>", em dash with a space on each side. */
export const CATALOG_SEP = ' — ';

export function regionName(r?: SoftCatalogRegion): string | undefined {
	return r?.label && r.label !== r.id ? r.label : undefined;
}

/** A LoxiLB node below this cannot load the patched connection table. */
export const MIN_PLAN_MEMORY_MB = 16384;

export function planTooSmall(p?: SoftCatalogType): boolean {
	return Boolean(p?.memoryMb) && (p?.memoryMb as number) < MIN_PLAN_MEMORY_MB;
}

/** "4 CPUs / 8 GB RAM", "1 CPU / 1 GB RAM", "... / 2 GPUs". */
export function planSpec(p?: SoftCatalogType): string | undefined {
	if (!p || !p.vcpus || !p.memoryMb) return undefined;
	const gb = p.memoryMb / 1024;
	const gbText = Number.isInteger(gb) ? String(gb) : gb.toFixed(1).replace(/\.0$/, '');
	let s = `${p.vcpus} ${p.vcpus === 1 ? 'CPU' : 'CPUs'} / ${gbText} GB RAM`;
	if (p.gpus && p.gpus > 0) s += ` / ${p.gpus} ${p.gpus === 1 ? 'GPU' : 'GPUs'}`;
	return s;
}

export function joinIdName(id: string, name?: string): string {
	return name ? `${id}${CATALOG_SEP}${name}` : id;
}

/** Sorted by name (then id). */
export function sortRegions(list: SoftCatalogRegion[]): SoftCatalogRegion[] {
	return [...list].sort(
		(a, b) => (a.label || a.id).localeCompare(b.label || b.id, 'en', {sensitivity: 'base'}) || a.id.localeCompare(b.id),
	);
}

/** Sorted by size: CPU, then RAM, then GPU, then id. */
export function sortPlans(list: SoftCatalogType[]): SoftCatalogType[] {
	return [...list].sort(
		(a, b) =>
			(a.vcpus || 0) - (b.vcpus || 0) ||
			(a.memoryMb || 0) - (b.memoryMb || 0) ||
			(a.gpus || 0) - (b.gpus || 0) ||
			a.id.localeCompare(b.id),
	);
}

/** Typing matches the id or the name (case-insensitive substring). */
export function matchesIdOrName(query: string, id: string, name?: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return id.toLowerCase().includes(q) || (name || '').toLowerCase().includes(q) || joinIdName(id, name).toLowerCase().includes(q);
}

/**
 * Names for the Soft-side fallback ids, used only when the catalog is not
 * loaded, so the default options still read "<id> — <name>".
 */
export const FALLBACK_REGIONS: SoftCatalogRegion[] = [{id: 'sg-sin-2', label: 'Singapore 2', country: 'sg'}];
export const FALLBACK_TYPES: SoftCatalogType[] = [
	{id: 'g6-dedicated-8', label: 'Dedicated 16 GB', class: 'dedicated', vcpus: 8, memoryMb: 16384, gpus: 0},
	{id: 'g8-dedicated-64-32', label: 'Dedicated 64 GB', class: 'dedicated', vcpus: 32, memoryMb: 65536, gpus: 0},
];

/** Catalog list when loaded, else the fallback list (ids limited to the fallback ids). */
export function withFallback<T extends {id: string}>(list: T[], fallback: T[], ids: readonly string[]): T[] {
	if (list.length) return list;
	return ids.map(id => fallback.find(x => x.id === id) ?? ({id, label: id} as unknown as T));
}
